import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { listReports, listScreenings, getDocumentDownload } from '../services/monitoringApi';
import GradeChip from './GradeChip';
import tokens from '../design/tokens';

/**
 * Reports and Screenings — the customer's PDFs, newest first, each with a
 * download. Read-only. The list never carries an S3 key: a download asks the
 * API for a 5-minute signed link by docId, then hands it to the browser.
 *
 * Reports are written by the weekly generator (onboarding repo,
 * lambda/motor-report/tools/weekly_report.py); screenings are attached by
 * staff (scripts/log-screening.mjs). The demo account's come from --refresh.
 */

const C = tokens.color;
const F = tokens.font;
const R = tokens.radius;
const MONO = { fontFamily: F.mono };
const DISP = { fontFamily: F.display };

const KINDS = {
  reports: {
    title: 'Reports',
    lede: 'Your weekly equipment health report, one per week, newest first.',
    empty: 'Your first report will appear here after your first full week of monitoring.',
    list: listReports,
  },
  screenings: {
    title: 'Screenings',
    lede: 'Every on-site screening of your equipment, with the full report.',
    empty: 'No screenings yet.',
    list: listScreenings,
  },
};

const day = (iso) => new Date(`${iso}T12:00:00Z`);
const fmtDay = (iso, opts) => (iso ? day(iso).toLocaleDateString('en-US', { timeZone: 'UTC', ...opts }) : '—');

/** "Sep 14 – 20, 2026", "Sep 28 – Oct 4, 2026", "Dec 28, 2026 – Jan 3, 2027". */
export function formatWeek(start, end) {
  if (!start || !end) return '—';
  const s = day(start), e = day(end);
  if (s.getUTCFullYear() !== e.getUTCFullYear()) {
    return `${fmtDay(start, { month: 'short', day: 'numeric', year: 'numeric' })} – ${fmtDay(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  const endPart = s.getUTCMonth() === e.getUTCMonth() ? fmtDay(end, { day: 'numeric' }) : fmtDay(end, { month: 'short', day: 'numeric' });
  return `${fmtDay(start, { month: 'short', day: 'numeric' })} – ${endPart}, ${e.getUTCFullYear()}`;
}

function motorsLine(d) {
  if (d.motorCount == null) return null;
  const m = `${d.motorCount} asset${d.motorCount === 1 ? '' : 's'}`;
  return d.reportingCount != null && d.reportingCount < d.motorCount ? `${m} · ${d.reportingCount} reporting` : m;
}

function Row({ kind, d, onDownload, busy, failed }) {
  const when = kind === 'reports'
    ? { label: 'Week', value: formatWeek(d.periodStart, d.periodEnd) }
    : { label: 'Screened', value: fmtDay(d.date, { month: 'short', day: 'numeric', year: 'numeric' }) };
  return (
    <li className="bs-doc" style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: 18, listStyle: 'none' }}>
      <div>
        <div style={{ ...MONO, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 4 }}>{when.label}</div>
        <div style={{ ...MONO, fontSize: 14, color: C.ink }}>{when.value}</div>
        {kind === 'screenings' && d.site && <div style={{ ...DISP, fontWeight: 700, fontSize: 15, color: C.ink, marginTop: 6 }}>{d.site}</div>}
      </div>
      <div className="bs-doc-meta" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {motorsLine(d) && <span style={{ ...MONO, fontSize: 13, color: C.text }}>{motorsLine(d)}</span>}
        {d.fleetGrade && <GradeChip grade={d.fleetGrade} title="Fleet health: the worst reporting asset" />}
      </div>
      <div className="bs-doc-action" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        <button type="button" onClick={() => onDownload(d)} disabled={busy}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: R.base, background: C.ink, border: 'none', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: F.body, opacity: busy ? 0.7 : 1, whiteSpace: 'nowrap' }}>
          <Download size={16} aria-hidden="true" /> {busy ? 'Preparing…' : 'Download PDF'}
        </button>
        {failed && <span role="alert" style={{ fontSize: 12, color: C.text }}>Couldn't start the download. Try again.</span>}
      </div>
    </li>
  );
}

export default function DocumentsPage({ kind, customerId, customerName, shopPhone }) {
  const k = KINDS[kind];
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [failedId, setFailedId] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await k.list(customerId);
      setDocs(res.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customerId, k]);

  useEffect(() => { load(); }, [load]);

  const onDownload = async (d) => {
    setBusyId(d.docId);
    setFailedId(null);
    try {
      const { url } = await getDocumentDownload(kind, d.docId, customerId);
      // The link is signed for 5 minutes and served as an attachment, so the
      // browser saves the PDF and the portal stays where it is.
      window.location.assign(url);
    } catch {
      setFailedId(d.docId);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        .bs-doc { display: grid; grid-template-columns: minmax(0,1.2fr) minmax(0,1fr) auto; gap: 20px; align-items: center; }
        @media (max-width: 560px) { .bs-doc { grid-template-columns: minmax(0,1fr); gap: 14px; } .bs-doc-action { align-items: stretch !important; } .bs-doc-action button { justify-content: center; } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ ...MONO, fontSize: 12, letterSpacing: F.tracking.eyebrow, color: C.link, textTransform: 'uppercase' }}>
            {customerName || 'Your plant'}
          </div>
          <h1 style={{ margin: 0, ...DISP, fontWeight: 700, fontSize: 30, letterSpacing: F.tracking.tight, color: C.ink }}>{k.title}</h1>
          <p style={{ margin: 0, fontSize: 14, color: C.textMuted }}>{k.lede}</p>
        </div>
        <button onClick={() => { setRefreshing(true); load(); }} disabled={refreshing}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: R.base, background: C.surface, border: `1px solid ${C.hairline}`, color: C.text, fontSize: 13, fontWeight: 600, cursor: refreshing ? 'default' : 'pointer', fontFamily: F.body, opacity: refreshing ? 0.6 : 1 }}>
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 76, borderRadius: R.xl }} />)}
        </div>
      ) : error ? (
        <div style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: 20, fontSize: 15, color: C.text }}>
          We couldn't load your {k.title.toLowerCase()}. Try Refresh{shopPhone ? `, or call us · ${shopPhone}` : ''}.
        </div>
      ) : docs.length === 0 ? (
        <div style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: 20, fontSize: 15, color: C.text }}>
          {k.empty}
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, display: 'grid', gap: 12 }}>
          {docs.map((d) => (
            <Row key={d.docId} kind={kind} d={d} onDownload={onDownload} busy={busyId === d.docId} failed={failedId === d.docId} />
          ))}
        </ul>
      )}
    </div>
  );
}
